import axios, { AxiosPromise, AxiosResponse } from 'axios'
import { BaseWfmNavbarItem } from 'c1-navbar/dist/navbarTypes'
import { ApiGatewayResponse } from '../../Helpers/apiGateway'
import { User } from '../../Permissions/AuthenticatedUser.context'
import { DEFAULT_LOGIN_ERROR_MESSAGE } from './login.constants'
import jwt_decode from 'jwt-decode'

export interface DevParams { redirectUri?: string }
export interface OIDCResponse { id_token: string }
export interface AuthLocation { authLocation: string }

export async function tryAuthenticateFR(code: string, devParams: DevParams, frLoginUrl: string): Promise<AxiosResponse<OIDCResponse>> {
  try {
    const authResponse = await axios.post<OIDCResponse>('/api/rest/auth/oidc', null, {
      params: {
        code,
        ...(devParams),
      },
    })
    if (authResponse.data?.id_token) {
      window.localStorage.setItem('FORGEROCK_ID_TOKEN', authResponse.data.id_token)
      window.history.pushState({}, '', window.location.origin)
    }
    return authResponse
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 403) {
      // If a 403 status code is returned then redirect the user to the login page.
      removeLocalStorageItems()
      window.location.href = `https://${frLoginUrl}`
      await new Promise(() => { /* Return a blank promise so that execution pauses while we wait for the window change */ })
    } else {
      window.history.pushState({}, '', window.location.origin)
    }
    throw e
  }
}

export async function getFRAuthLocation(devParams: DevParams): Promise<AxiosResponse<unknown>> {
  const locationResp = await axios.get('/api/rest/auth/location', { params: { ...(devParams) } })
  window.localStorage.setItem('authLocation', locationResp.data.authLocation)
  window.localStorage.setItem('forgerockLoginUrl', locationResp.data.forgerockLoginUrl)
  window.localStorage.setItem('FR_ClIENT_ID', locationResp.data.forgerockClientId)
  window.localStorage.setItem('FR_OP_URL', locationResp.data.forgerockHost)
  window.localStorage.setItem('FR_REALM', locationResp.data.forgerockRealm)
  return locationResp
}

export const tryAuthenticateOnPrem = (): AxiosPromise<User> => axios.get('/api/rest/authorize')

export function parseJwt(token: string | null): null | $TSFIXME {
  if (!token) {
    return null
  }
  try {
    return jwt_decode(token)
  } catch {
    return null
  }
}
export interface SystemProperties { value?: string }
export async function getGatewayUrl(): Promise<AxiosResponse<SystemProperties>> {
  const response = await axios.get('/api/system/properties?key=iamGatewayHost')
  return response.data.value
}
export interface WFMMenuItem {
  name: string,
  isPermitted: boolean,
  url: string,
}
export interface WfmConfigC1 { navItems: Record<string, BaseWfmNavbarItem>, wfmBaseUrl: string, error: string }
export interface WfmGlobalNav { Items: Record<string, BaseWfmNavbarItem> }

const customErrorMessageWFM = (httpErrorStatus: number) => {
  if (httpErrorStatus === 401) {
    return 'noAccessToWFM'
  } else if (httpErrorStatus === 403) {
    return 'noRolesInWFM'
  } else {
    return 'errorLoadingContent'
  }
}

export async function getWfmConfigFromApiGateway(gatewayUrl: string, forgerockIdToken: string): Promise<WfmConfigC1> {
  let wfmBaseUrl = ''
  if (forgerockIdToken) {
    const parsedToken = parseJwt(forgerockIdToken)
    wfmBaseUrl = parsedToken?.appTenant?.twfm?.url ?? ''
  }
  try {
    const url = `https://${gatewayUrl}/proxy/twfm/api/query/globalnavigation/currentglobalnavigation`
    const token = forgerockIdToken ?  { headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${forgerockIdToken}`,
    } } : {}
    const resp = await axios.post<ApiGatewayResponse<WfmGlobalNav>>(url, {}, token)
    const navItems = resp.data.Result[0].Items
    if (navItems?.dataExplorer) {
      // WFM navbar item is now handled by mixing in permissions
      delete navItems.dataExplorer
    }

    return {
      navItems: navItems ?? {},
      wfmBaseUrl,
      error: '',
    }
  } catch (e: $TSFIXME) {
    const httpErrorStatus = e.response.status
    const error = customErrorMessageWFM(httpErrorStatus)
    return { navItems: {},  wfmBaseUrl, error }
  }
}

export interface GlobalUser { permissions: string[] }
export async function getCurrentGlobalUser(gatewayUrl: string, forgerockIdToken: string): Promise<GlobalUser> {
  try {
    const url = `https://${gatewayUrl}/proxy/twfm/api/query/GlobalUser/CurrentGlobalUser`
    const token = forgerockIdToken ?  { headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${forgerockIdToken}`,
    } } : {}
    const resp = await axios.post(url, {}, token)
    return resp.data.Result[0]
  } catch (e) {
    return { permissions: [] }
  }
}
export interface TenantSummary { id: number, name: string }
export interface C1AuthSuccess { user: User }
export interface C1AuthTenantConflict { tenants: TenantSummary[] }
interface C1AuthParams {
  auth: 'c1' | 'saml',
  tenantId?: string,
  userId?: string,
}

interface C1SamlOnLoadAuthParams {
  idp: string,
}

export async function singleSignOnOnPageLoad(idp: string): Promise<AxiosPromise<User>> {
  const params: C1SamlOnLoadAuthParams = { idp }

  return await axios.get<User>('/api/rest/authorize', { params })
}

export async function login(userId: string, password: string, tenantId: string | null = null, locale = 'en'): Promise<C1AuthSuccess | C1AuthTenantConflict> {
  const data = {
    userId,
    password,
    locale,
    selectedLanguage: locale,
    tenantId: tenantId === '' ? null : tenantId,
  }
  const params: C1AuthParams = { auth: 'c1' }

  try {
    const response = await axios.post('/api/rest/authorize', data, { params })

    const user = response.data
    return { user }
  } catch (err: $TSFIXME) {
    const response = err.response
    const errorMessage = response.data.errorMessage || response.statusText || DEFAULT_LOGIN_ERROR_MESSAGE

    if (response.status === 409) {
      // conflict; multiple tenants
      const tenants = JSON.parse(errorMessage)

      return { tenants }
    } else {
      throw new Error(errorMessage)
    }
  }
}

export async function singleSignOn(userId: string, tenantId: string | null = null): Promise<C1AuthSuccess | C1AuthTenantConflict | null> {
  const params: C1AuthParams = {
    userId,
    auth: 'saml',
  }
  if (tenantId) {
    params.tenantId = tenantId
  }

  try {
    const response = await axios.get<User>('/api/rest/authorize', { params })
    const status = response.status
    const user = response.data

    if (status === 278 && response.headers.location) {
      location.href = response.headers.location

      await new Promise(() => { /* Return a blank promise so that execution pauses while we wait for the window change */ })
      return null
    }

    return { user }
  } catch (err: $TSFIXME) {
    if (err.response) {
      const status = err.response.status
      const errorMessage = err.response.data.errorMessage

      if (status === 409) {
        // conflict; multiple tenants
        const tenants = JSON.parse(errorMessage)

        return { tenants }
      } else {
        throw new Error(errorMessage)
      }
    } else {
      throw err
    }
  }
}

export async function logoutDx(): Promise<void> {
  try {
    // This will return a 302 but we dont want to do anything with it
    await axios.get('/advancedreporting/logout.jsp?c1=true')
  } catch {
    // There's nothing we can do if this errors
    // This will 100% error in dev due to CORS
  }
}

export const removeLocalStorageItems = () => {
  window.localStorage.removeItem('FORGEROCK_ID_TOKEN')
  window.localStorage.removeItem('FR_OP_URL')
  window.localStorage.removeItem('FR_REALM')
  window.localStorage.removeItem('forgerockLoginUrl')
  window.localStorage.removeItem('authLocation')
  window.localStorage.removeItem('wasAuthFR')
  window.localStorage.removeItem('FR_ClIENT_ID')
}

export const logoutApi = async (forgerockIdToken: string, frLoginUrl: string, frOpUrl: string, frRealm: string, wasAuthFR: boolean): Promise<void> => {
  removeLocalStorageItems()
  const deleteC1SessionPromise = axios.delete('/api/rest/authorize')
  try {
    if (wasAuthFR) {
      await deleteC1SessionPromise
      if (forgerockIdToken && forgerockIdToken !== 'reAuth') {
        await axios.get(`https://${frOpUrl}/am/oauth2${frRealm}/connect/endSession?id_token_hint=${forgerockIdToken}`) // delete FR session
      }

      let redirectLoginUrl = `https://${frLoginUrl}?redirect_to_aic=true`
      const subDomainLoginUrl = sessionStorage.getItem('subDomainLoginUrl')
      sessionStorage.removeItem('subDomainLoginUrl')
      if (subDomainLoginUrl) {
        try {
          // Validate the URL
          const url = new URL(subDomainLoginUrl)
          redirectLoginUrl = url.href
        } catch (e) {
          // Do nothing, keep the original frLoginUrl
        }
      }

      window.location.href = redirectLoginUrl

      await new Promise(() => { /* Return a blank promise so that execution pauses while we wait for the window change */ })
    } else {
      const { headers, status } = await deleteC1SessionPromise
      if (status === 278 && headers.location) {
        location.href = headers.location
        await new Promise(() => { /* Return a blank promise so that execution pauses while we wait for the window change */ })
      }
    }
  } catch {
    // fail silently
  }
}

export interface PasswordResetResult { email: string, expiration: number }
export async function sendPasswordResetEmail(address: string): Promise<PasswordResetResult> {
  const response = await axios.post<PasswordResetResult>('/api/password/reset', {
    email: address,
  })
  return response.data
}

export interface AuthenticationConfig { allowedAuthenticationTypes: string[] }
export async function getAuthConfig(): Promise<AuthenticationConfig> {
  const response = await axios.get<AuthenticationConfig>('/api/rest/iam/auth/config')
  return response.data
}
